import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { BookOpen, Plus, X, Loader2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

/**
 * V13.6 — Admin assigns recorded courses to a learner from the user-detail
 * modal. Pairs with the existing batch-assignment UX. Backend endpoints:
 *   POST /api/users/{user_id}/assign-course/{course_id}
 *   DELETE /api/users/{user_id}/assign-course/{course_id}
 *   GET /api/users/{user_id}/assigned-courses
 */
export default function CoursesTabPanel({ userId, userName }) {
  const [assigned, setAssigned] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [pickValue, setPickValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/api/users/${userId}/assigned-courses`),
        api.get('/api/courses', { params: { status: 'published', limit: 100 } }),
      ]);
      setAssigned(r1.data.courses || []);
      setAllCourses(r2.data.courses || r2.data || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const assignedIds = new Set(assigned.map(c => c.id));
  const available = allCourses.filter(c => !assignedIds.has(c.id));

  const assign = async () => {
    if (!pickValue) return;
    setBusy(true);
    try {
      await api.post(`/api/users/${userId}/assign-course/${pickValue}`);
      const c = allCourses.find(x => x.id === pickValue);
      toast.success(`Assigned ${c?.title || 'course'} to ${userName || 'learner'}`);
      setPickValue('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assign failed');
    }
    setBusy(false);
  };

  const unassign = async (courseId, title) => {
    if (!window.confirm(`Remove access to "${title}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/api/users/${userId}/assign-course/${courseId}`);
      toast.success('Course access removed');
      load();
    } catch {
      toast.error('Remove failed');
    }
    setBusy(false);
  };

  if (loading) return <div className="py-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="user-courses-panel">
      {/* Picker */}
      <div className="flex items-center gap-2">
        <Select value={pickValue} onValueChange={setPickValue}>
          <SelectTrigger className="flex-1 text-xs h-8" data-testid="user-courses-picker">
            <SelectValue placeholder={available.length === 0 ? "No more courses to assign" : "Select a recorded course"} />
          </SelectTrigger>
          <SelectContent position="popper" sideOffset={4} className="z-[300]">
            {available.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">All available courses are already assigned.</div>
            ) : available.map(c => (
              <SelectItem key={c.id} value={c.id}>
                {c.title} <span className="text-muted-foreground">— {c.course_type}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={assign} disabled={!pickValue || busy} data-testid="user-courses-assign-btn">
          <Plus className="h-3 w-3 mr-1" />Assign
        </Button>
      </div>

      {/* Assigned list */}
      {assigned.length === 0 ? (
        <div className="text-center py-10 border border-dashed rounded-2xl text-muted-foreground">
          <BookOpen className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No recorded courses assigned yet.</p>
          <p className="text-xs mt-1">Assign one from the dropdown above so this learner sees it under <strong>My Courses</strong>.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {assigned.map(c => (
            <div key={c.id} className="flex items-center justify-between rounded-xl border p-3 hover:bg-muted/30 transition" data-testid={`user-course-row-${c.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {c.thumbnail_url ? (
                  <img src={c.thumbnail_url} alt="" className="h-12 w-16 rounded-md object-cover" />
                ) : (
                  <div className="h-12 w-16 rounded-md bg-muted flex items-center justify-center"><BookOpen className="h-5 w-5 text-muted-foreground" /></div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{c.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="secondary" className="text-[10px]">{c.course_type}</Badge>
                    <span className="text-[11px] text-muted-foreground">{c.episode_count || 0} episode{c.episode_count !== 1 ? 's' : ''}</span>
                    {c.status === 'draft' && <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">Draft</Badge>}
                  </div>
                  {c.assigned_at && (
                    <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1" data-testid={`user-course-assigned-${c.id}`}>
                      <Calendar className="h-3 w-3" />
                      Assigned on {formatDate(c.assigned_at)}
                    </p>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => unassign(c.id, c.title)} disabled={busy}
                      data-testid={`user-course-remove-${c.id}`}>
                <X className="h-3 w-3 mr-1" />Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
